/**
 * Full-screen overlay shown while a TanStack mutation is in flight.
 *
 * Why this exists: when the user confirms a destructive action (delete
 * a channel, stop monitoring an X account), the mutation runs but the
 * list cache only invalidates and refetches after the network call
 * resolves. In that window the user sees the row they just "deleted"
 * still in the list, which is confusing. A semi-transparent
 * backdrop + spinner makes it obvious the page is mid-operation and
 * blocks any further clicks until the mutation settles.
 *
 * Debounced by 200ms so fast mutations (sub-second) don't cause a
 * spinner flash — the overlay only appears when something is
 * actually slow enough for the user to notice.
 */
import { useEffect, useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export function LoadingOverlay() {
  const mutating = useIsMutating();
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation('common');

  useEffect(() => {
    if (mutating > 0) {
      const id = setTimeout(() => setVisible(true), 200);
      return () => clearTimeout(id);
    }
    setVisible(false);
  }, [mutating]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3 rounded-2xl bg-white px-6 py-5 shadow-xl">
        <div className="h-6 w-6 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
        <p className="text-sm font-medium text-gray-700">
          {t('workingOnIt', { defaultValue: 'Working…' })}
        </p>
      </div>
    </div>
  );
}
